import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Heading,
} from '@chakra-ui/react'
import React, { type ChangeEvent, type Dispatch, type SetStateAction } from 'react'

import chakraTokenToVar from '@/utils/chakraTokenToVar'

import CourseQ from '@/queries/CourseQ'
import TopicQ from '@/queries/TopicQ'
import type { Query } from '@/client/query'

import { type FilterBy, useUpdateSelectedTopics } from '@/components/Course/helpers'
import Tippy from '@/components/UI/Tippy'

import TopicFilterCheckbox from './TopicFilterCheckbox'

const TopicFilter: React.FC<{
  course: Query
  disabled: boolean
  setPageDisabled: Dispatch<SetStateAction<boolean>>
  isModal?: boolean
  selectedTopics: Set<number>
  selectedStaging: {
    topics: Set<number>
    type: FilterBy
  }
  setSelectedStaging: Dispatch<
    SetStateAction<{
      topics: Set<number>
      type: FilterBy
    }>
  >
}> = ({
  course,
  disabled,
  setPageDisabled,
  isModal = false,
  selectedTopics,
  selectedStaging,
  setSelectedStaging,
}) => {
  const updateSelectedTopics = useUpdateSelectedTopics(setPageDisabled)

  const topics = course.many(
    CourseQ.getTopics({
      excludeChallenge: true,
    })
  )

  const handleChange = React.useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const topicId = e.target.name
      const dupTopicSet = isModal ? new Set(selectedStaging.topics) : new Set(selectedTopics)

      if (e.target.checked === true) {
        dupTopicSet.add(Number.parseInt(topicId, 10))
        if (isModal) {
          setSelectedStaging({
            topics: dupTopicSet,
            type: selectedStaging.type,
          })
          return
        }
        updateSelectedTopics(Array.from(dupTopicSet))
        return
      }

      dupTopicSet.delete(Number.parseInt(topicId, 10))
      if (isModal) {
        setSelectedStaging({
          topics: dupTopicSet,
          type: selectedStaging.type,
        })
        return
      }
      updateSelectedTopics(Array.from(dupTopicSet))
    },
    [selectedStaging, setSelectedStaging, selectedTopics, updateSelectedTopics, isModal]
  )

  return (
    <Accordion
      defaultIndex={[0]}
      allowToggle
      borderBottom={`2px solid ${chakraTokenToVar('border')}`}
    >
      <AccordionItem>
        <Tippy content="Collapse/Uncollapse Topics">
          <AccordionButton
            bgColor="transparent"
            border="none"
            px={0}
            pt={0}
            _hover={{
              cursor: 'pointer',
            }}
            _focus={{
              outline: 'none',
            }}
          >
            <Box flex={1} textAlign="left">
              <Heading size="lg" as="h4" mb="6px">
                Topics
              </Heading>
            </Box>
            <AccordionIcon />
          </AccordionButton>
        </Tippy>
        <AccordionPanel maxH="calc(100vh - 244px)" overflowY="auto" p={0}>
          {/* Subtract combined height of Nav header, Topics title, and Type filter */}
          {topics.map(topic => (
            <TopicFilterCheckbox
              key={topic(TopicQ.id) ?? 0}
              topic={topic}
              selectedTopics={isModal ? selectedStaging.topics : selectedTopics}
              onCheck={handleChange}
              disabled={disabled}
            />
          ))}
        </AccordionPanel>
      </AccordionItem>
    </Accordion>
  )
}

export default TopicFilter
